import useChangeCompany from '@/hooks/useChangeCompany.js';
import useCompanyId from '@/hooks/useCompanyId.js';
import setPath from '@/lib/setPath.js';
import { getObjectCompany, getUserCompaniesOptions } from '@/redux/actions/userActions.js';
import selectors from '@/redux/selectors/selectors.js';
import routes from '@/routes.js';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import waitForSyncReady from './redux/actions/waitForSyncReady.js';
import { useArgSelector } from './redux/curriedSelectors/curriedSelectors.js';

const PlatformRedirectHandler = () => {
  const changeCompany = useChangeCompany();
  const { companyId, setSessionCompanyId } = useCompanyId();
  const activeRouteId = useSelector((state) => state.ui.activeRouteId);
  const activeRouteReady = useSelector((state) => state.ui.activeRouteReady);
  const jobId = useArgSelector(selectors.param, 'jobId');
  const workflowId = useArgSelector(selectors.param, 'workflowId');
  const isPlatformLoading = useArgSelector(selectors.pref, 'platformLoading')?.isLoading;
  const activeJob = useSelector((state) => selectors.jobs(state)?.find((n) => n.id === jobId));
  const activeWorkflow = useSelector((state) => selectors.workflows(state)?.find((n) => n.id === workflowId));
  const flowStarted = useSelector(selectors.signupFlowStarted);
  const userId = useSelector(selectors.userId);

  const activeRouteSlug = routes.find((n) => n.id === activeRouteId)?.routeId;
  const isWorkflowScreen = activeRouteSlug === 'workflows';
  const isJobScreen = activeRouteSlug === 'jobs';
  const isRedirectScreen = isWorkflowScreen || isJobScreen;
  const activeObjectId = isWorkflowScreen ? workflowId : jobId;
  const activeObject = isWorkflowScreen ? activeWorkflow : activeJob;
  const rootPath = isWorkflowScreen ? '/workflows' : isJobScreen ? '/jobs' : undefined;

  // If the signup flow has started, we want to redirect to the signup screen
  useEffect(() => {
    if (flowStarted && window.location.pathname !== '/signup') {
      setPath('/signup');
    }
  }, [flowStarted]);

  // If:
  // - We are on a job or workflow screen,
  // - The route is ready (data loaded),
  // - We expect a workflow or job from the URL
  // - We don't have the active object in the state
  //
  // Then we want to check if we're in the right company, and change if not.
  useEffect(() => {
    if (!activeRouteReady || !isRedirectScreen || !activeObjectId || activeObject || isPlatformLoading) {
      return;
    }

    const objectId = isWorkflowScreen ? workflowId : jobId;
    const objectType = isWorkflowScreen ? 'workflow' : 'job';

    const fetchObjectCompany = async () => {
      await waitForSyncReady();
      await getObjectCompany({ objectId, objectType }, (payload, error) => {
        const objectIsDeleted = payload?.isDeleted;
        if (objectIsDeleted && rootPath) {
          setPath(rootPath);
        }

        // If it belongs to an unauthorized company, redirect to the root
        // of jobs or workflows.
        if (error) {
          if (rootPath) {
            setPath(rootPath);
          }
          return;
        }

        // If the object belongs to a different authorized company, change the company.
        if (payload?.companyId && payload?.companyId !== companyId) {
          setSessionCompanyId(payload.companyId);
          return changeCompany(payload.companyId, { message: 'Please wait while we change companies...', redirect: false }, () => {
            getUserCompaniesOptions({ userId });
          });
        }
      });
    };

    fetchObjectCompany();
  }, [isRedirectScreen, activeRouteReady, activeObject, activeObjectId, companyId]);

  // Just a little hack for development to allow pasting production/staging URLs directly in localhost
  useEffect(() => {
    if (window.location.hostname === 'localhost') {
      const path = window.location.pathname;
      const search = window.location.search;
      const match = path.match(/^\/https?:\/\/(localhost:\d+|staging\.mindcloud\.co|app\.mindcloud\.co)(\/.*)/);
      if (match) {
        const actualPath = match[2];
        setPath(actualPath + search);
      }
    }
  }, []);

  return null;
};

export default PlatformRedirectHandler;
